/**
 * GraphTreePickerModal — V2 port of V1 ``AddFolderModal`` (May 25, 2026).
 *
 * Shared OneDrive + SharePoint picker. Microsoft doesn't ship a v2 file
 * picker that can browse all sites under a tenant, so V1 builds a
 * custom Graph-tree browser. V2 mirrors that 1:1.
 *
 * Modes:
 *   - ``"onedrive"``    — single-step. Folder-tree view rooted at the
 *                          user's personal OneDrive ``root``.
 *   - ``"sharepoint"``  — three-step. Pick a site → pick a drive in
 *                          that site → walk the folder tree under that
 *                          drive's root. Steps are radio-selected so
 *                          drilling back up is a one-click undo.
 *
 * Multi-select via checkboxes on every folder node. The picker
 * intentionally surfaces FOLDERS only (V1 parity for OneDrive/SP — V2
 * users still get standalone-file tracking via the unified ``Aggiungi``
 * → Google Drive flow when their file isn't OneDrive-bound).
 *
 * Resolves a ``PickedItem[]`` (all ``is_folder: true``) on confirm; an
 * empty selection on cancel.
 */

import type { DriveProvider } from "@ando/core";
import {
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  cn,
} from "@ando/ui";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  AlertCircle,
  ChevronRight,
  Folder,
  Globe,
  HardDrive,
  Loader2,
  RefreshCw,
} from "lucide-react";
import * as React from "react";

import {
  useDriveFolderTree,
  useSharePointDrives,
  useSharePointFolderTree,
  useSharePointSites,
} from "@/features/sync/hooks/useDriveSync";

import type { PickedItem } from "./types";

export interface GraphTreePickerModalProps {
  readonly open: boolean;
  /**
   * Provider del picker.
   * - ``"onedrive"`` — MS Graph folder-tree (single step).
   * - ``"sharepoint"`` — 3-step site → drive → folder-tree.
   * - ``"google_drive"`` (giugno 11, 2026) — Google Drive folder-tree
   *   via ``/drive-management/folder-tree-cached`` (richiede scope
   *   ``drive`` o ``drive.readonly``, NON ``drive.file``). Sostituisce
   *   il Google Picker JS che era limitato dallo scope per-file.
   */
  readonly mode: "onedrive" | "sharepoint" | "google_drive";
  readonly connectionKey?: string;
  readonly onPick: (items: readonly PickedItem[]) => void;
  readonly onCancel: () => void;
}

export const GraphTreePickerModal: React.FC<GraphTreePickerModalProps> = ({
  open,
  mode,
  connectionKey,
  onPick,
  onCancel,
}) => {
  const { t } = useLingui();
  // Single-step tree (no site/drive prelude) per OneDrive e Google
  // Drive; multi-step solo per SharePoint.
  const isTreeOnly = mode === "onedrive" || mode === "google_drive";
  const provider: DriveProvider = mode === "sharepoint" ? "sharepoint" : mode;

  // ── Selection state ──────────────────────────────────────────────
  // Map<folderId, {name, path}> so the confirm callback can hand the
  // ``DocSyncManager`` enough metadata to populate ``select-folders``
  // without a follow-up Graph call.
  const [selected, setSelected] = React.useState<Map<string, { name: string; path?: string }>>(
    () => new Map(),
  );

  // ── SharePoint multi-step state ──────────────────────────────────
  const [siteId, setSiteId] = React.useState<string | null>(null);
  const [driveId, setDriveId] = React.useState<string | null>(null);

  // Reset state on every open so reusing the modal doesn't leak
  // selections / steps across separate picker invocations.
  React.useEffect(() => {
    if (open) {
      setSelected(new Map());
      setSiteId(null);
      setDriveId(null);
    }
  }, [open]);

  // ── SharePoint pre-step queries ──────────────────────────────────
  const sites = useSharePointSites(connectionKey, mode === "sharepoint" && open);
  const drives = useSharePointDrives(mode === "sharepoint" ? siteId : null, connectionKey);

  // ── Folder-tree query ────────────────────────────────────────────
  // OneDrive + Google Drive: ``useDriveFolderTree`` via
  // ``/drive-management/folder-tree-cached`` — BE walks il root del
  // provider. Per Google Drive (giugno 11, 2026) richiede scope
  // ``drive`` o ``drive.readonly`` (NON ``drive.file``).
  //
  // SharePoint: ``useSharePointFolderTree`` via ``/drive/files`` — BE
  // rejects SharePoint on ``folder-tree-cached`` (validation error
  // "requires selecting a site first") so V1 + V2 use the dedicated
  // SharePoint files endpoint, filter folders client-side, lift into
  // the shared ``FolderTreeResponse`` shape via
  // ``sharePointFilesToFolderTree``. Single-level for parity with V1
  // ``buildSharePointFolderTree``.
  //
  // ``forceRefresh`` flips a ``true`` automatically quando la prima
  // response è una cache EMPTY stale (il BE serve snapshot fino a 27
  // giorni via ``drive_folder_cache.py``). SharePoint path NON
  // cacha — every fetch is live.
  const [needsForceRefresh, setNeedsForceRefresh] = React.useState(false);
  const treeQuery = useDriveFolderTree(
    provider,
    connectionKey,
    undefined,
    isTreeOnly,
    needsForceRefresh,
  );
  const sharepointTree = useSharePointFolderTree(
    mode === "sharepoint" ? siteId : null,
    mode === "sharepoint" ? driveId : null,
    connectionKey,
    mode === "sharepoint" && !!driveId,
  );
  const tree = mode === "sharepoint" ? sharepointTree : treeQuery;
  React.useEffect(() => {
    if (needsForceRefresh) return; // already retried
    if (!tree.data || tree.isLoading) return;
    const isEmpty = (tree.data.roots?.length ?? 0) === 0 && (tree.data.nodes?.length ?? 0) === 0;
    // ``stale`` / ``cached`` live on the typed ``FolderTreeResponse``
    // now (June 5 2026 — surfaced through ``flattenBackendTree``).
    if (isEmpty && (tree.data.stale || tree.data.cached)) {
      setNeedsForceRefresh(true);
    }
  }, [tree.data, tree.isLoading, needsForceRefresh]);

  const toggleFolder = (id: string, name: string, path?: string): void => {
    setSelected((s) => {
      const next = new Map(s);
      if (next.has(id)) next.delete(id);
      else next.set(id, { name, path });
      return next;
    });
  };

  // Jun 16, 2026 — recursive sync scope summary. Tracking a folder
  // syncs ALL its descendants; the picker now surfaces the total file
  // count in the footer + (when scope is large) gates the confirm
  // behind a one-line acknowledgement so a misclick on a root folder
  // (the "Ando Central" incident) can't silently kick off hundreds of
  // workflows. ``LARGE_SCOPE_THRESHOLD`` is conservative — at 50 the
  // user has probably picked a single deep folder on purpose; above,
  // we require an explicit nod.
  const LARGE_SCOPE_THRESHOLD = 50;
  const nodeById = React.useMemo(() => {
    const m = new Map<string, PickerNode>();
    for (const n of (tree.data?.nodes ?? []) as PickerNode[]) m.set(n.id, n);
    return m;
  }, [tree.data?.nodes]);
  const selectedFileCount = React.useMemo(() => {
    let total = 0;
    for (const id of selected.keys()) {
      const n = nodeById.get(id);
      total += n?.descendant_file_count ?? 0;
    }
    return total;
  }, [selected, nodeById]);
  const isLargeScope = selectedFileCount >= LARGE_SCOPE_THRESHOLD;
  const [scopeAcknowledged, setScopeAcknowledged] = React.useState(false);
  // Reset the ack the moment the scope shrinks below threshold so a
  // user who unselects can confirm again without a stale ack.
  React.useEffect(() => {
    if (!isLargeScope) setScopeAcknowledged(false);
  }, [isLargeScope]);

  const handleConfirm = (): void => {
    if (isLargeScope && !scopeAcknowledged) return;
    const items: PickedItem[] = Array.from(selected.entries()).map(([id, { name, path }]) => ({
      id,
      name,
      is_folder: true,
      provider,
      folder_path: path,
      parent_folder_id: null,
    }));
    onPick(items);
  };

  // ── Step indicator (SharePoint only) ─────────────────────────────
  const sharePointStep: 1 | 2 | 3 = !siteId ? 1 : !driveId ? 2 : 3;

  // Manual "Aggiorna" affordance — visible while viewing the folder
  // tree step (OneDrive single-step OR SharePoint step 3) so the user
  // can force-refresh if the auto-retry didn't help (e.g. cache filled
  // with stale BUT non-empty data that's now out of date upstream).
  const showRefreshButton = isTreeOnly || (mode === "sharepoint" && !!driveId);

  // Etichette/glifi per-provider — single source of truth invece di
  // ternari sparsi nel JSX. Aggiungere un nuovo provider single-step
  // = nuova entry qui + estendere ``mode`` nel type.
  const providerLabel: Record<typeof mode, { name: string; ariaTitle: string }> = {
    onedrive: {
      name: "OneDrive",
      ariaTitle: t`Aggiungi cartelle da OneDrive`,
    },
    sharepoint: {
      name: "SharePoint",
      ariaTitle: t`Aggiungi cartelle da SharePoint`,
    },
    google_drive: {
      name: "Google Drive",
      ariaTitle: t`Aggiungi cartelle da Google Drive`,
    },
  };
  const label = providerLabel[mode];
  const handleRefresh = (): void => {
    setNeedsForceRefresh(true);
    void tree.refetch();
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
    >
      <DrawerContent
        side="right"
        ariaTitle={label.ariaTitle}
        className="flex w-[min(480px,95vw)] flex-col sm:max-w-none"
      >
        <DrawerHeader>
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-md bg-ando-orange-soft text-ando-orange">
              {mode === "sharepoint" ? (
                <Globe className="h-4 w-4" strokeWidth={2} />
              ) : (
                <HardDrive className="h-4 w-4" strokeWidth={2} />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="truncate font-display font-semibold text-[15px] text-ink-1">
                <Trans>Aggiungi da {label.name}</Trans>
              </h2>
              <p className="truncate text-[11.5px] text-ink-3">
                {mode === "sharepoint" ? (
                  <>
                    <Trans>Step {sharePointStep}/3</Trans> ·{" "}
                    {sharePointStep === 1
                      ? t`Scegli il sito`
                      : sharePointStep === 2
                        ? t`Scegli la raccolta`
                        : t`Scegli le cartelle (sync ricorsivo)`}
                  </>
                ) : (
                  t`Spunta = sync ricorsivo di tutti i file dentro`
                )}
              </p>
            </div>
            {showRefreshButton ? (
              <button
                type="button"
                onClick={handleRefresh}
                disabled={tree.isFetching}
                aria-label={t`Aggiorna struttura cartelle`}
                title={t`Aggiorna struttura cartelle`}
                className={cn(
                  "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
                  "text-ink-3 transition-colors hover:bg-black/[0.04] hover:text-ink-1",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                )}
              >
                <RefreshCw
                  className={cn("h-3.5 w-3.5", tree.isFetching && "animate-spin")}
                  aria-hidden
                />
              </button>
            ) : null}
          </div>
        </DrawerHeader>

        <DrawerBody className="flex-1 overflow-y-auto px-5 py-4">
          {mode === "sharepoint" && sharePointStep === 1 ? (
            <SiteList
              sites={sites.data}
              isLoading={sites.isLoading}
              error={sites.error}
              onRetry={() => void sites.refetch()}
              onPick={setSiteId}
            />
          ) : mode === "sharepoint" && sharePointStep === 2 ? (
            <>
              <BackButton onClick={() => setSiteId(null)}>
                <Trans>← Siti</Trans>
              </BackButton>
              <DriveList
                drives={drives.data}
                isLoading={drives.isLoading}
                error={drives.error}
                onRetry={() => void drives.refetch()}
                onPick={setDriveId}
              />
            </>
          ) : (
            <>
              {mode === "sharepoint" ? (
                <BackButton onClick={() => setDriveId(null)}>
                  <Trans>← Raccolte</Trans>
                </BackButton>
              ) : null}
              <FolderTreeList
                isLoading={tree.isLoading}
                error={tree.error}
                onRetry={() => void tree.refetch()}
                roots={tree.data?.roots ?? []}
                nodes={tree.data?.nodes ?? []}
                selected={selected}
                onToggle={toggleFolder}
              />
            </>
          )}
        </DrawerBody>

        <DrawerFooter className="flex flex-col gap-2">
          {/* Scope summary — cartelle selezionate + totale file ricorsivi.
              Jun 16, 2026: aggiunto per evitare il caso "ho cliccato la
              checkbox della root pensando di espandere" → silenziosamente
              sync di centinaia di file. Sopra la soglia, gate del Conferma
              dietro un acknowledgement esplicito. */}
          {selected.size > 0 ? (
            <div
              className={cn(
                "flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[11.5px]",
                isLargeScope
                  ? "border-ando-orange/40 bg-ando-orange-soft/40 text-ink-1"
                  : "border-hairline bg-card/40 text-ink-2",
              )}
            >
              <Folder className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />
              <span className="min-w-0 flex-1">
                <strong className="font-medium tabular-nums">{selected.size}</strong>{" "}
                {selected.size === 1 ? <Trans>cartella</Trans> : <Trans>cartelle</Trans>}
                {selectedFileCount > 0 ? (
                  <>
                    {" · "}
                    <strong className="font-medium tabular-nums">~{selectedFileCount}</strong>{" "}
                    <Trans>file (sync ricorsivo)</Trans>
                  </>
                ) : null}
              </span>
            </div>
          ) : (
            <p className="text-[11.5px] text-ink-3">
              <Trans>Nessuna cartella selezionata</Trans>
            </p>
          )}
          {isLargeScope ? (
            <label className="flex cursor-pointer items-start gap-2 rounded-md bg-ando-orange-soft/30 px-2.5 py-2 text-[11.5px] text-ink-1">
              <Checkbox
                className="mt-0.5"
                checked={scopeAcknowledged}
                onChange={(e) => setScopeAcknowledged(e.target.checked)}
              />
              <span className="leading-snug">
                <Trans>
                  Capisco che Ando sincronizzerà ~{selectedFileCount} file di tutte le
                  sotto-cartelle delle{" "}
                  {selected.size === 1 ? t`cartella selezionata` : t`cartelle selezionate`}.
                </Trans>
              </span>
            </label>
          ) : null}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md px-3 py-1.5 font-medium text-[12px] text-ink-2 hover:bg-black/[0.04]"
            >
              <Trans>Annulla</Trans>
            </button>
            <button
              type="button"
              disabled={selected.size === 0 || (isLargeScope && !scopeAcknowledged)}
              onClick={handleConfirm}
              className={cn(
                "flex-1 rounded-md bg-ando-orange px-3 py-1.5 font-medium text-[12px] text-white",
                "hover:bg-ando-orange/90 disabled:cursor-not-allowed disabled:bg-ink-3/30 disabled:text-ink-3",
              )}
            >
              {selected.size === 0
                ? t`Seleziona almeno una cartella`
                : isLargeScope && !scopeAcknowledged
                  ? t`Spunta sopra per procedere`
                  : t`Aggiungi ${selected.size} · ~${selectedFileCount} file`}
            </button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
GraphTreePickerModal.displayName = "GraphTreePickerModal";

// ──────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────

const BackButton: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="mb-3 inline-flex items-center text-[11.5px] text-ando-orange hover:underline"
  >
    {children}
  </button>
);

// ──────────────────────────────────────────────────────────────────────
// PickerError — shared error surface for the 3 query branches (sites /
// drives / folder-tree). Without this, a 401/403/network error showed
// as the same generic "Nessun sito accessibile" copy as a legitimately
// empty result — user couldn't tell "no permissions / token scaduto"
// apart from "really no sites" (V1 audit G6 root cause, June 4 2026).
// The component surfaces the BE error message verbatim when available
// + a Retry CTA that triggers the underlying TanStack refetch.
// ──────────────────────────────────────────────────────────────────────
const PickerError: React.FC<{
  readonly title: string;
  readonly error: unknown;
  readonly onRetry: () => void;
}> = ({ title, error, onRetry }) => {
  const { t } = useLingui();
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : t`Errore sconosciuto durante il caricamento. Verifica la connessione o riconnetti l'account.`;
  return (
    <div
      role="alert"
      className="flex flex-col gap-2 rounded-md border border-error/30 bg-error/[0.06] p-3"
    >
      <div className="flex items-center gap-2">
        <AlertCircle className="h-3.5 w-3.5 shrink-0 text-error" aria-hidden />
        <p className="font-medium text-[12px] text-ink-1">{title}</p>
      </div>
      <p className="text-[11px] text-ink-3 leading-relaxed">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex w-fit items-center gap-1 rounded-md bg-error/10 px-2 py-1 font-medium text-[11px] text-error hover:bg-error/15"
      >
        <RefreshCw className="h-3 w-3" aria-hidden />
        <Trans>Riprova</Trans>
      </button>
    </div>
  );
};

const SiteList: React.FC<{
  sites: readonly { id: string; name: string; web_url?: string | null }[] | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  onPick: (id: string) => void;
}> = ({ sites, isLoading, error, onRetry, onPick }) => {
  const { t } = useLingui();
  return error ? (
    <PickerError
      title={t`Impossibile caricare i siti SharePoint`}
      error={error}
      onRetry={onRetry}
    />
  ) : isLoading ? (
    <p className="text-[12px] text-ink-3">
      <Trans>Carico siti SharePoint…</Trans>
    </p>
  ) : !sites || sites.length === 0 ? (
    // June 5 2026 — distinguish "nessun sito" empty-state from the
    // permissions/MSA case. SharePoint does NOT exist for Microsoft
    // PERSONAL accounts (MSA: outlook.com / hotmail.com / live.com) —
    // it's a Microsoft 365 / Azure AD feature. The BE logs
    // ``"This API is not supported for MSA accounts"`` when this
    // happens; the FE can't distinguish that without echoing the BE
    // diagnostic so we present the most likely cause + a hint.
    <div className="flex flex-col gap-2 rounded-md border border-hairline bg-card/50 p-3">
      <p className="font-medium text-[12px] text-ink-1">
        <Trans>Nessun sito accessibile</Trans>
      </p>
      <p className="text-[11px] text-ink-3 leading-relaxed">
        <Trans>
          SharePoint richiede un account Microsoft 365 / Azure AD (work o school). Gli account
          Microsoft personali (outlook.com / hotmail.com / live.com) non hanno SharePoint.
        </Trans>
      </p>
      <p className="text-[11px] text-ink-3 leading-relaxed">
        <Trans>
          Se hai un account aziendale, ricollegalo dalla pagina Integrazioni. Se invece serve
          OneDrive, scegli il connettore dedicato.
        </Trans>
      </p>
    </div>
  ) : (
    <ul className="flex flex-col gap-1">
      {sites.map((s) => (
        <li key={s.id}>
          <button
            type="button"
            onClick={() => onPick(s.id)}
            className="flex w-full items-center gap-2 rounded-md border border-hairline bg-card/50 px-3 py-2 text-left hover:bg-card/85"
          >
            <Globe className="h-3.5 w-3.5 text-ink-3" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12.5px] text-ink-1">{s.name}</p>
              {s.web_url ? <p className="truncate text-[10.5px] text-ink-3">{s.web_url}</p> : null}
            </div>
            <ChevronRight className="h-3 w-3 text-ink-3" />
          </button>
        </li>
      ))}
    </ul>
  );
};

const DriveList: React.FC<{
  drives: readonly { id: string; name: string; drive_type?: string }[] | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  onPick: (id: string) => void;
}> = ({ drives, isLoading, error, onRetry, onPick }) => {
  const { t } = useLingui();
  return error ? (
    <PickerError title={t`Impossibile caricare le raccolte`} error={error} onRetry={onRetry} />
  ) : isLoading ? (
    <p className="text-[12px] text-ink-3">
      <Trans>Carico raccolte…</Trans>
    </p>
  ) : !drives || drives.length === 0 ? (
    <p className="text-[12px] text-ink-3">
      <Trans>Nessuna raccolta disponibile.</Trans>
    </p>
  ) : (
    <ul className="flex flex-col gap-1">
      {drives.map((d) => (
        <li key={d.id}>
          <button
            type="button"
            onClick={() => onPick(d.id)}
            className="flex w-full items-center gap-2 rounded-md border border-hairline bg-card/50 px-3 py-2 text-left hover:bg-card/85"
          >
            <HardDrive className="h-3.5 w-3.5 text-ink-3" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12.5px] text-ink-1">{d.name}</p>
              {d.drive_type ? <p className="text-[10.5px] text-ink-3">{d.drive_type}</p> : null}
            </div>
            <ChevronRight className="h-3 w-3 text-ink-3" />
          </button>
        </li>
      ))}
    </ul>
  );
};

// Shape consumed inside the picker tree. ``descendant_file_count`` is
// the post-order tally surfaced by ``flattenBackendTree`` (Jun 16, 2026)
// so the picker can render the recursive sync scope on each node.
type PickerNode = {
  id: string;
  name: string;
  parent_id?: string | null;
  children_count?: number;
  has_children?: boolean;
  already_tracked?: boolean;
  descendant_file_count?: number;
};

const FolderTreeList: React.FC<{
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  roots: readonly string[];
  nodes: readonly PickerNode[];
  selected: Map<string, { name: string; path?: string }>;
  onToggle: (id: string, name: string, path?: string) => void;
}> = ({ isLoading, error, onRetry, roots, nodes, selected, onToggle }) => {
  const { t } = useLingui();
  if (error) {
    return (
      <PickerError
        title={t`Impossibile caricare la struttura cartelle`}
        error={error}
        onRetry={onRetry}
      />
    );
  }
  // Build a lightweight parent → children map so we can render a tree
  // without recursive props drilling. Lazy expansion is left to a
  // future iteration — V1 also loaded the whole tree at once for
  // OneDrive (only Graph SharePoint paginated drives, but the BE relay
  // flattens that).
  const childrenMap = React.useMemo(() => {
    const m = new Map<string | null, typeof nodes>();
    for (const n of nodes) {
      const key = n.parent_id ?? null;
      const arr = (m.get(key) ?? []) as Array<(typeof nodes)[number]>;
      arr.push(n);
      m.set(key, arr);
    }
    return m;
  }, [nodes]);

  const [expanded, setExpanded] = React.useState<Set<string>>(() => new Set());
  const toggleExpand = (id: string): void =>
    setExpanded((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-[12px] text-ink-3">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <Trans>Carico struttura cartelle…</Trans>
      </div>
    );
  }
  if (nodes.length === 0) {
    return (
      <p className="text-[12px] text-ink-3">
        <Trans>Nessuna cartella trovata in questa posizione.</Trans>
      </p>
    );
  }

  const rootNodes =
    roots.length > 0 ? nodes.filter((n) => roots.includes(n.id)) : (childrenMap.get(null) ?? []);

  return (
    <ul role="tree" className="flex flex-col gap-0.5">
      {rootNodes.map((root) => (
        <TreeNode
          key={root.id}
          node={root}
          depth={0}
          childrenMap={childrenMap}
          expanded={expanded}
          selected={selected}
          onToggleExpand={toggleExpand}
          onToggleSelect={onToggle}
        />
      ))}
    </ul>
  );
};

const TreeNode: React.FC<{
  node: PickerNode;
  depth: number;
  childrenMap: ReadonlyMap<string | null, readonly PickerNode[]>;
  expanded: Set<string>;
  selected: Map<string, { name: string; path?: string }>;
  onToggleExpand: (id: string) => void;
  onToggleSelect: (id: string, name: string, path?: string) => void;
}> = ({ node, depth, childrenMap, expanded, selected, onToggleExpand, onToggleSelect }) => {
  const { t } = useLingui();
  const kids = childrenMap.get(node.id) ?? [];
  const hasChildren = node.has_children !== false && kids.length > 0;
  const isExpanded = expanded.has(node.id);
  const isSelected = selected.has(node.id);
  const isAlreadyTracked = node.already_tracked === true;
  // Jun 16, 2026 — surface recursive sync scope on every node. A user
  // clicked the root "Ando Central" checkbox intending to expand it,
  // and the picker silently tracked ~310 descendant files. Showing the
  // count next to each folder makes the scope obvious BEFORE the click.
  const fileCount = node.descendant_file_count ?? 0;
  return (
    <li role="treeitem" aria-selected={isSelected}>
      <div
        className={cn(
          "flex items-center rounded-md hover:bg-black/[0.03]",
          isSelected && "bg-ando-orange-soft/40",
          isAlreadyTracked && "opacity-60",
        )}
        style={{ paddingLeft: depth * 16 }}
      >
        {/* Tap target 1: SELECT — checkbox with 28×28 hit area so it can't
            be hit by accident when reaching for the chevron. */}
        <label
          className={cn(
            "grid h-7 w-7 shrink-0 place-items-center rounded-md",
            "cursor-pointer hover:bg-black/[0.06]",
            isAlreadyTracked && "cursor-not-allowed",
          )}
          aria-label={
            isSelected
              ? t`Deseleziona ${node.name}`
              : fileCount > 0
                ? t`Seleziona ${node.name} (~${fileCount} file)`
                : t`Seleziona ${node.name}`
          }
        >
          <Checkbox
            checked={isSelected || isAlreadyTracked}
            disabled={isAlreadyTracked}
            onChange={() => onToggleSelect(node.id, node.name)}
          />
        </label>
        {/* Visual divider so checkbox vs expand-row are physically
            distinct — Jun 16, 2026 fix for the "clicked checkbox by
            mistake while trying to expand" UX trap. */}
        <span className="mx-1 h-4 w-px shrink-0 bg-black/[0.06]" aria-hidden />
        {/* Tap target 2: EXPAND — entire row body. Clicking the folder
            name or chevron toggles expansion (Finder/Explorer mental
            model). Falls back to a non-interactive span when leaf. */}
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggleExpand(node.id)}
            aria-label={isExpanded ? t`Comprimi ${node.name}` : t`Espandi ${node.name}`}
            aria-expanded={isExpanded}
            className={cn(
              "flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1.5 text-left",
              "hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
            )}
          >
            <ChevronRight
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-ink-3 transition-transform",
                isExpanded && "rotate-90",
              )}
              aria-hidden
            />
            <Folder className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />
            <span className="min-w-0 flex-1 truncate text-[12px] text-ink-1">{node.name}</span>
            {fileCount > 0 ? (
              <span
                className={cn(
                  "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] tabular-nums",
                  isSelected ? "bg-ando-orange/15 text-ando-orange" : "bg-black/[0.05] text-ink-3",
                )}
                title={t`${fileCount} file inclusi nel sync ricorsivo`}
              >
                ↳ {fileCount}
              </span>
            ) : null}
            {isAlreadyTracked ? (
              <span className="shrink-0 text-[10px] text-ink-3">
                <Trans>tracciata</Trans>
              </span>
            ) : null}
          </button>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-2 px-1.5 py-1.5">
            <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <Folder className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />
            <span className="min-w-0 flex-1 truncate text-[12px] text-ink-1">{node.name}</span>
            {fileCount > 0 ? (
              <span
                className={cn(
                  "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] tabular-nums",
                  isSelected ? "bg-ando-orange/15 text-ando-orange" : "bg-black/[0.05] text-ink-3",
                )}
                title={t`${fileCount} file`}
              >
                {fileCount}
              </span>
            ) : null}
            {isAlreadyTracked ? (
              <span className="shrink-0 text-[10px] text-ink-3">
                <Trans>tracciata</Trans>
              </span>
            ) : null}
          </div>
        )}
      </div>
      {hasChildren && isExpanded ? (
        <ul role="group" className="flex flex-col gap-0.5">
          {kids.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              childrenMap={childrenMap}
              expanded={expanded}
              selected={selected}
              onToggleExpand={onToggleExpand}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
};
